'use client';
import { Minus, Plus } from '@phosphor-icons/react';
import React, { useEffect, useId, useRef, useState } from 'react';
import CompactButton from '../CompactButton/CompactButton';
import { LanguageType, LanguageDirection } from '@/lib/types';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export interface FAQAccordionProps {
	title: string;    // The text displayed on the header button.
	content: string;  // The content shown when the accordion is expanded.
	language?: LanguageType;
	children?: React.ReactElement<{ color?: string, size?: number, className?: string }>;
	shouldAnimate?: boolean
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

const FAQAccordion: React.FC<FAQAccordionProps> = ({
	title,
	content,
	language = "english",
	shouldAnimate = true
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const isLg = useIsLg();
	const accordionRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLParagraphElement>(null);
	const triggerId = useId();
	const panelId = useId();

	// When the panel opens, move screen-reader focus to the answer text so it
	// gets read out automatically. `aria-expanded` alone only announces the
	// state change ("expanded"), not the now-revealed content.
	// `preventScroll` keeps the GSAP scroll-trigger choreography intact —
	// without it the browser would scroll the panel into view.
	useEffect(() => {
		if (isOpen) {
			requestAnimationFrame(() => {
				contentRef.current?.focus({ preventScroll: true });
			});
		}
	}, [isOpen]);

	// Add blur mask reveal animation
	useGSAP(() => {
		const accordion = accordionRef.current;
		if (!accordion) return;

		if (shouldAnimate) {
			// Set initial state
			gsap.set(accordion, {
				autoAlpha: 0,
				// filter: 'blur(30px)',
				y: +100,
				force3D: true,
			});

			// Create scroll trigger animation - runs only once
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: accordion,
					start: "bottom bottom",
					toggleActions: "play none none none"
				}
			});

			// Animate to final state
			tl.to(accordion, {
				autoAlpha: 1,
				delay: 0.2,
				// filter: 'blur(0px)',
				y: 0,
				duration: 1.0,
				ease: "power2.out",
				force3D: true,
			});
		}

	}, { dependencies: [title, content], revertOnUpdate: true });

	const handleClose = () => {
		setIsOpen(false);
	};

	// TODO: fix the triggers whenever something expands!!
	useEffect(() => {
		setTimeout(() => {
			ScrollTrigger.refresh();
		}, 600); // 600ms is the duration of the animation
	}, [isOpen]);

	return (
		<div
			// -> fortate drept margin/padding top pe answer
			// gap-[2rem]
			// lg:gap-[3.5rem]
			ref={accordionRef}
			className={`
			w-full

			flex flex-col 

			pb-[1.25rem]
			lg:pb-[1.5rem]
			border-b-main
			border-b-stroke-soft-200


			`}
			style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>

			<button
				type="button"
				id={triggerId}
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={() => isOpen ? handleClose() : setIsOpen(true)}
				className='
				w-full flex flex-row items-start justify-between 
				gap-[1.5rem]
				group
				px-0
				lg:px-[0.5rem]
				cursor-pointer
				'>
				<div
					// onClick={() => isOpen ? handleClose() : setIsOpen(true)}
					style={{
						direction: LANGUAGE_TO_DIRECTION[language],
					}}
					className={`
				        flex items-center justify-between
						text-22-to-18--alexandria
						${isOpen ?
							`text-text-tertiary`
							:
							`text-text-tertiary group-hover:text-text-quinary`
						}
				            `}
				>
					<div className='flex flex-row gap-[0.5rem] items-center justify-center py-[0.25rem] lg:py-[0.625rem]'>
						<span className={`${language === 'english' ? 'text-left' : 'text-right'}`}>
							{title || 'Dropdown'}
						</span>
					</div>
				</div>

				<div className='flex items-center justify-center'>
					<CompactButton size={isLg ? 56 : 36} radius={9999}
						// onClick={() => isOpen ? handleClose() : setIsOpen(true)}
						onClick={() => { }}
						as='div'
						className="relative"
					>
						<div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
							<Minus
								size={isLg ? 24 : 16}
								className={`absolute transition-all duration-[700ms] ease-in-out
								text-graphics-secondary
								${isOpen
										? 'opacity-100 rotate-0'
										: 'opacity-0 rotate-90'
									}`}
							/>
							<Plus
								size={isLg ? 24 : 16}
								className={`absolute transition-all duration-[700ms] ease-in-out
								text-graphics-secondary
								${isOpen
										? 'opacity-0 rotate-90'
										: 'opacity-100 rotate-0'
									}`}
							/>
						</div>
					</CompactButton>
				</div>
			</button>


			{/* Expanded content */}
			<div
				id={panelId}
				role="region"
				aria-labelledby={triggerId}
				aria-hidden={!isOpen || undefined}
				className={`
					w-full
					flex flex-col gap-[1rem]

					transition-all duration-500 ease-out
					origin-top
					overflow-hidden
					opacity-0
					max-h-0
					${isOpen
						? 'opacity-100 max-h-fit pb-[0.75rem] lg:pb-[1rem] pt-[1.5rem] lg:pt-[2.5rem]'
						: 'opacity-0 max-h-0'}

					${isLg ? 'px-[0.5rem]' : ''}
				`}>
				<p
					ref={contentRef}
					tabIndex={-1}
					className="text-20--alexandria text-text-tertiary focus:outline-none"
					style={{ whiteSpace: 'pre-line' }}
				>
					{content}
				</p>
			</div>
		</div>
	);
};

export default FAQAccordion;
